import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import NavigationHeader from '../../components/NavigationHeader';
import SectionScrollIndicator from '../../components/SectionScrollIndicator';
import ContactHero from './components/ContactHero';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import ContactMap from './components/ContactMap';
import Icon from '../../components/AppIcon';

const ContactResume = () => {
  useEffect(() => {
    document.title = 'Contact & Resume - Dishant Madariya | Full Stack Developer';

    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  const socialLinks = [
    // {
    //   name: 'LinkedIn',
    //   icon: 'Linkedin',
    //   url: 'https://www.linkedin.com/in/dishantmadariya/',
    //   color: 'text-blue-600',
    //   description: 'Professional network & endorsements'
    // },
    {
      name: 'GitHub',
      icon: 'Github',
      url: 'https://github.com/DishantMadariya',
      color: 'text-gray-800 dark:text-gray-200',
      description: 'Code repositories & contributions'
    },
    {
      name: 'Twitter',
      icon: 'Twitter',
      url: 'https://twitter.com/dishant_patel14',
      color: 'text-blue-400',
      description: 'Tech insights & industry updates'
    },
    // {
    //   name: 'Stack Overflow',
    //   icon: 'HelpCircle',
    //   url: 'https://stackoverflow.com/users/alexdeveloper',
    //   color: 'text-orange-500',
    //   description: 'Community contributions'
    // },
    {
      name: 'Instagram',
      icon: 'Instagram',
      url: 'https://www.instagram.com/dishantmadariya._14/',
      color: 'text-purple-600',
      description: 'Personal life & updates'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Contact & Resume - Dishant Madariya | Full Stack Developer</title>
        <meta
          name="description"
          content="Contact Full Stack Developer Dishant Madariya, view resume, and discuss React, Node.js, WordPress or SaaS projects and collaborations."
        />
        <meta
          name="keywords"
          content="contact Dishant Madariya, hire full stack developer, resume, React developer contact, Node.js developer contact"
        />
        <link rel="canonical" href="https://port-folio-three-teal.vercel.app/contact" />
      </Helmet>
      <SectionScrollIndicator />

      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-12 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <ContactHero />
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12 lg:py-0">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Form - Left Side (2/3 width on desktop) */}
              <div className="lg:col-span-2" id="contact-form">
                <ContactForm />
                <div className="bg-surface rounded-lg border border-border p-6 mt-8">
                  <h3 className="text-lg font-semibold text-text-primary mb-4">Connect With Me</h3>

                  <div className="grid grid-cols-1 gap-3">
                    {socialLinks.map((social, index) => (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted transition-colors duration-200 group"
                      >
                        <div className="w-10 h-10 bg-muted group-hover:bg-background rounded-lg flex items-center justify-center">
                          <Icon name={social.icon} size={18} className={social.color} />
                        </div>

                        <div className="flex-1">
                          <p className="font-medium text-text-primary group-hover:text-primary transition-colors duration-200">
                            {social.name}
                          </p>
                          <p className="text-sm text-text-secondary">{social.description}</p>
                        </div>

                        <Icon name="ExternalLink" size={16} className="text-text-secondary group-hover:text-primary transition-colors duration-200" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Contact Info - Right Side (1/3 width on desktop) */}
              <div className="lg:col-span-1">
                <ContactInfo />
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-12 lg:py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
                Location &amp; Availability
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Based in Surat, India and available for local meetings as well as remote collaboration worldwide.
                Let&apos;s connect to discuss your project or just say hello.
              </p>
            </div>

            <ContactMap />
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-text-secondary">
                Quick answers to common questions about working together
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="HelpCircle" size={20} color="var(--color-primary)" className="mt-1" />
                  <div>
                    <h3 className="font-semibold text-text-primary mb-2">What's your typical response time?</h3>
                    <p className="text-sm text-text-secondary">
                      I respond to all inquiries within 24 hours, usually much sooner.
                      For urgent matters, I'm available via phone or LinkedIn.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="DollarSign" size={20} color="var(--color-primary)" className="mt-1" />
                  <div>
                    <h3 className="font-semibold text-text-primary mb-2">How do you handle project pricing?</h3>
                    <p className="text-sm text-text-secondary">
                      Pricing depends on project scope, timeline, and complexity.
                      I provide detailed quotes after understanding your requirements.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="Calendar" size={20} color="var(--color-primary)" className="mt-1" />
                  <div>
                    <h3 className="font-semibold text-text-primary mb-2">What's your availability?</h3>
                    <p className="text-sm text-text-secondary">
                      Currently available for new projects with immediate start possible.
                      I work with 2-3 clients simultaneously to ensure quality focus.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-start space-x-3">
                  <Icon name="Globe" size={20} color="var(--color-primary)" className="mt-1" />
                  <div>
                    <h3 className="font-semibold text-text-primary mb-2">Do you work with remote clients?</h3>
                    <p className="text-sm text-text-secondary">
                      Absolutely! I work with clients worldwide and have experience
                      managing projects across different time zones effectively.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-12 lg:py-16 bg-gradient-to-r from-primary/5 to-accent/5">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Don't let your great ideas wait. Let's discuss how we can bring your vision to life
              with cutting-edge technology and exceptional user experience.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:contact.dishantpatel@gmail.com"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="Mail" size={18} className="mr-2" />
                Send Email Now
              </a>

              <a
                href="tel:+15551234567"
                className="inline-flex items-center justify-center px-6 py-3 bg-surface border border-border text-text-primary rounded-lg font-medium hover:bg-muted transition-colors duration-200"
              >
                <Icon name="Phone" size={18} className="mr-2" />
                Call Directly
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ContactResume;